import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Chip,
    IconButton,
    Pagination,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Button,
    useTheme,
} from '@mui/material';
import {
    Close as CloseIcon,
    FilterList as FilterListIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { API_SERVICE_CREDITS } from 'src/config';
import { Scrollbar } from 'src/components/scrollbar';
import getBorderColor from 'src/utils/get-border-color';
import PaginationStand from '../PaginationStand';

const CREDIT_TYPES = [
    { value: 'ai.bdr', label: 'AI BDR' },
    { value: 'ai.gp', label: 'AI General Purpose' },
    { value: 'contact.email.export', label: 'Contact Email Export' },
    { value: 'contact.email.lookup', label: 'Contact Email Lookup' },
    { value: 'contact.email.validate', label: 'Email Validation' },
    { value: 'contact.phone.export', label: 'Contact Phone Export' },
    { value: 'contact.phone.lookup', label: 'Contact Phone Lookup' },
    { value: 'send.email', label: 'Send Email' },
    { value: 'send.multichannel', label: 'Send Multichannel' },
    { value: 'send.warmup', label: 'Send Warmup' },
    { value: 'contact.lookups', label: 'Contact Lookups' },
    { value: 'contact.imports', label: 'Contact Imports' },
    { value: 'contact.exports', label: 'Contact Exports' },
    { value: 'contact.enrichment', label: 'Contact Enrichment' },
    { value: 'ai.voice.minutes', label: 'AI Voice Minutes' },
    { value: 'phone.numbers.minutes', label: 'Phone Numbers Minutes' },
];

const CreditsLogsModal = ({ open, onClose }) => {
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const [logsPerPage, setLogsPerPage] = useState(25);

    // Filter states
    const [filterType, setFilterType] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [shouldFetch, setShouldFetch] = useState(false);
    const theme = useTheme();

    const userEmail = sessionStorage.getItem('userEmail');

//     useEffect(() => {
//   if (open) {
//     fetchCreditsLogs(1);
//   }
// }, [open]);

    useEffect(() => {
        if (open) {
            fetchCreditsLogs(currentPage);
            // setShouldFetch(false);
        }
    }, [open, currentPage, logsPerPage]);

    const fetchCreditsLogs = async (
        page, 
        type = filterType,
        start = startDate,
        end = endDate
        ) => {
        setIsLoading(true);
        try {
            const params = {
                email: userEmail,
                page: page,
                limit: logsPerPage
            };

            // Add optional filters
            if (type) {
                params.type = type;
            }
            if (start) {
                params.startDate = new Date(start).toISOString();
            }
            if (end) {
                params.endDate = new Date(end).toISOString();
            }

            const response = await axios.get(
                `${API_SERVICE_CREDITS}/credit-logs`,
                { params }
            );

            if (response.data.success) {
                setLogs(response.data.data.logs || []);
                setTotalPages(response.data.data.pagination.pages);
                setTotalLogs(response.data.data.pagination.total);
                setCurrentPage(page);
            } else {
                toast.error(response.data.message || 'Failed to fetch credits logs');
            }
        } catch (error) {
            console.error('Error fetching credits logs:', error);
            toast.error('Failed to fetch credits logs');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
        //fetchCreditsLogs(page);
    };

    const handleApplyFilters = () => {
        setCurrentPage(1);
         fetchCreditsLogs(1,filterType, startDate, endDate);
        // setShouldFetch(true);
    };

    const handleClearFilters = () => {
        setFilterType('');
        setStartDate('');
        setEndDate('');
        setCurrentPage(1);
        // Fetch logs without filters
        // setTimeout(() => {
        //     fetchCreditsLogs(1);
        // }, 0);
        fetchCreditsLogs(1, '', '', '');
        
    };

    const getTypeColor = (type) => {
        const colors = {
            'ai.bdr': 'secondary',
            'ai.gp': 'primary',
            'contact.email.export': 'info',
            'contact.email.lookup': 'info',
            'contact.email.validate': 'success',
            'contact.phone.export': 'info',
            'contact.phone.lookup': 'info',
            'send.email': 'warning',
            'send.multichannel': 'warning',
            'send.warmup': 'warning',
            'contact.lookups': 'info',
            'contact.imports': 'info',
            'contact.exports': 'info',
            'contact.enrichment': 'success',
            'ai.voice.minutes': 'secondary',
            'phone.numbers.minutes': 'secondary',
        };
        return colors[type] || 'default';
    };

    const formatCreditType = (type) => {
        if (!type) return 'Unknown';
        const found = CREDIT_TYPES.find(ct => ct.value === type);
        return found ? found.label : type.split('.').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { minHeight: '70vh' }
            }}
        >
            <DialogTitle sx={{mb:0}}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" fontWeight="bold">
                        Credits Consumption Logs
                    </Typography>
                    <IconButton onClick={onClose} size="small" sx={{ color: theme => theme.palette.text.primary }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent
                // dividers
                sx={{
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',

                }}
            >
                {/* Filters */}

                <Box sx={{ mt:1,mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <FormControl size="small"
                        sx={{
                            minWidth: 200,
                            marginLeft: "4px",
                            "& .MuiInputBase-root": {
                                height: 40,
                            },
                        }}
                        className='glass-dropdown'
                    >
                        <InputLabel>Filter by Type</InputLabel>
                        <Select
                            value={filterType}
                            label="Filter by Type"
                            onChange={(e) => setFilterType(e.target.value)}
                        >
                            <MenuItem value="">All Types</MenuItem>
                            {CREDIT_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            minWidth: 160,
                            color: theme.palette.text.primary,
                            "& .MuiInputBase-root": {
                                height: 40,
                            },
                        }}
                        className='glass-input'
                    />

                    <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ 
                            minWidth: 160, 
                            color: theme.palette.text.primary,
                        "& .MuiInputBase-root": {
                                height: 40,
                            },
                         }}
                        className='glass-input'
                    />

                    <Button
                        variant="contained"
                        onClick={handleApplyFilters}
                        startIcon={<FilterListIcon />}
                        disabled={isLoading}
                        sx={{
                            "& .MuiInputBase-root": {
                                height: 40,
                            },
                        }}
                    >
                        Apply
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={handleClearFilters}
                        className='glass-button-secondary-50'
                        sx={{
                            "& .MuiInputBase-root": {
                                height: 40,
                            },
                        }}
                        disabled={isLoading || (!filterType && !startDate && !endDate)}
                    >
                        Clear
                    </Button>
                </Box>
                {/* </Box> */}

                {/* Loading State */}
                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Table */}
                        {logs.length > 0 ? (
                            <>
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        //borderRadius: "12px",
                                        overflow: "hidden",
                                        backgroundColor: "white",
                                        border: "1px solid rgba(224, 224, 229, 1)",
                                        borderRadius: 1,
                                    }}
                                    className="glass-wrapper-50"
                                >
                                    <TableContainer
                                        sx={{
                                            // height: "100%",
                                            maxHeight: "65vh",
                                            overflowY: 'auto',
                                            "& .simplebar-mask":
                                            {
                                                zIndex: "auto",
                                            },
                                            "& .simplebar-content": {
                                                minHeight: "100%",
                                                display: "block",
                                            },
                                        }}
                                        component={Scrollbar}
                                        //component={Paper}
                                        id="scrollableDiv"
                                        className="glass-scrollbar"
                                    >
                                        <Table stickyHeader>
                                            <TableHead className="glass-table-header"
                                                sx={{
                                                    "& th": { p: 2 },
                                                    "& .MuiTableCell-root": {
                                                        backgroundColor: "rgb(233, 233, 236)",
                                                        color: theme.palette.text.primary,
                                                    },
                                                }}>
                                                <TableRow
                                                // sx={{ backgroundColor: 'action.hover' }}
                                                >
                                                    <TableCell><strong>Date & Time</strong></TableCell>
                                                    <TableCell><strong>Type</strong></TableCell>
                                                    <TableCell><strong>Description</strong></TableCell>
                                                    <TableCell ><strong>Amount</strong></TableCell>
                                                    <TableCell><strong>Request ID</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {logs.map((log, index) => (
                                                    <TableRow
                                                        key={log._id || index}

                                                        sx={{
                                                            cursor: 'pointer',
                                                            '&:hover': { backgroundColor: 'transparent' },
                                                            '& td, & th': (theme) => ({
                                                                borderBottom: `1px solid ${getBorderColor(
                                                                    theme.palette.mode
                                                                )}`,
                                                            }),
                                                        }}
                                                    >
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {new Date(log.createdAt).toLocaleDateString()}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {new Date(log.createdAt).toLocaleTimeString()}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={formatCreditType(log.type)}
                                                                color={getTypeColor(log.type)}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {log.description || '—'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="bold"
                                                                color="error.main"
                                                            >
                                                                -{log.amount.toLocaleString()}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontFamily: 'monospace',
                                                                    fontSize: '0.75rem'
                                                                }}
                                                            >
                                                                {log.requestId ? log.requestId.slice(0, 12) + '...' : '—'}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>

                            </>
                        ) : (
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                minHeight="300px"
                            >
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    No logs found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {filterType || startDate || endDate
                                        ? 'Try adjusting your filters'
                                        : 'Your credits consumption history will appear here'}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
            {/* Pagination */}
            {/* {totalPages > 1 && ( */}
            {logs.length > 0 && 
            // totalPages > 1 && 
            (
                <Box mt={0} >


                    <PaginationStand
                        loading={isLoading}
                        page={currentPage}
                        totalPages={totalPages}
                        totalRecords={totalLogs}
                        limit={logsPerPage}
                        onPageChange={(e, newPage) => {
                            handlePageChange(e, newPage);
                        }}
                        onLimitChange={(e) => {
                            const newLimit = Number(e.target.value);
                            setLogsPerPage(newLimit);
                            setCurrentPage(1);
                            // fetchCreditsLogs(1); 
                        }}
                        showLimitSelector={true}
                        RowsSelectorComponent={null}
                        insideModal={true}
                    />


                </Box>
            )}
            {/* <DialogActions sx={{ px: 3, py: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
                    Showing {logs.length} of {totalLogs} logs
                </Typography>
            </DialogActions> */}
        </Dialog>
    );
};

export default CreditsLogsModal;